import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { tenders as tendersApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Gavel, Clock, Sparkles, Award, Truck, CheckCircle2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

function fmtCountdown(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s.toString().padStart(2, '0')}s left`;
}

const qualityLabel = { premium: 'Premium', standard: 'Standard', economy: 'Economy' };

export default function Tenders() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(0);
  const [acceptFor, setAcceptFor] = useState(null);
  const [sp] = useSearchParams();
  const focus = sp.get('focus');

  const load = useCallback(async () => {
    try { setItems(await tendersApi.mine()); } catch (e) { console.warn('[tenders] load failed', e?.response?.status); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/'); return; }
    load();
    const poll = setInterval(load, 5000);
    const ti = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(ti); };
  }, [user, loading, nav, load]);

  if (loading || !user) return <div className="max-w-5xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Your live bargains</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">Quote requests</h1>
          <p className="text-ink/70 mt-1 text-sm">Shop owners bid privately — you see only the best deal.</p>
        </div>
        <Link to="/tender/new" data-testid="new-tender-link" className="btn-primary"><Gavel className="w-4 h-4"/>Ask another quote</Link>
      </div>

      {items.length === 0 ? (
        <div className="card-soft p-12 text-center mt-8">
          <Gavel className="w-12 h-12 mx-auto text-ink/30"/>
          <div className="font-heading font-bold text-xl mt-3">No quote requests yet</div>
          <p className="text-ink/60 mt-1 text-sm">Send your first request — let dukandaars compete on price.</p>
          <Link to="/tender/new" className="btn-primary inline-flex mt-5">Request a quote</Link>
        </div>
      ) : (
        <div className="mt-7 space-y-4" key={tick /* trigger countdown re-render */}>
          {items.map(t => (
            <TenderCard key={t.id} tender={t} focus={focus === t.id} onAccept={() => setAcceptFor(t)}/>
          ))}
        </div>
      )}

      <AcceptDialog tender={acceptFor} onClose={() => setAcceptFor(null)} onSuccess={(orderId) => { setAcceptFor(null); toast.success('Order placed with winning shop!'); nav(`/orders/${orderId}`); }}/>
    </div>
  );
}

function TenderCard({ tender, focus, onAccept }) {
  const best = tender.best_bid;
  const expired = new Date(tender.expires_at).getTime() <= Date.now();
  const open = tender.status === 'open' && !expired;
  const awarded = tender.status === 'awarded';
  return (
    <article data-testid={`tender-${tender.id}`} className={`card-soft p-5 ${focus ? 'ring-2 ring-[#E67E22]' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-xl">{tender.product_name}</h3>
            <span className={`pill !text-[10px] ${awarded ? '!bg-[#E8F5E9] !text-[#1B5E20]' : open ? '!bg-[#FFF3E0] !text-[#9A4500]' : '!bg-muted !text-ink/50'}`}>{awarded ? 'Awarded' : open ? 'Live' : 'Closed'}</span>
          </div>
          <div className="text-xs text-ink/60 mt-1">{tender.quantity} × {tender.unit} · {tender.category} · {tender.village}</div>
          {tender.items && tender.items.length > 0 && (
            <div className="mt-2 text-xs text-ink/70">+ {tender.items.length} more item{tender.items.length > 1 ? 's' : ''}: <span className="font-medium">{tender.items.map(i => `${i.name} ×${i.quantity}`).join(', ')}</span></div>
          )}
          {tender.description && <div className="text-sm text-ink/80 mt-1.5">{tender.description}</div>}
        </div>
        {open && (
          <div className="text-right">
            <div className="inline-flex items-center gap-1 text-sm font-semibold text-ink/70"><Clock className="w-4 h-4 text-[#E67E22]"/>{fmtCountdown(tender.expires_at)}</div>
            <div className="text-[10px] uppercase tracking-wider text-ink/40 mt-0.5">{best ? `${best.bidders} shop${best.bidders > 1 ? 's' : ''} bidding` : 'Waiting for bids…'}</div>
          </div>
        )}
      </div>

      {/* Best deal showcase */}
      {best ? (
        <div data-testid={`best-bid-${tender.id}`} className="mt-5 rounded-xl border-2 border-[#2E7D32] bg-[#F1F8F2] p-5">
          <div className="flex items-center gap-2 text-[#1B5E20] text-xs uppercase tracking-[0.2em] font-bold">
            <Sparkles className="w-3.5 h-3.5"/>Best deal so far <span className="text-ink/40 text-[10px] normal-case tracking-normal ml-1">(shop hidden until you accept)</span>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3">
            <div>
              <div className="font-heading font-black text-4xl text-ink">₹{best.effective_price}</div>
              <div className="text-xs text-ink/60">per {tender.unit}{tender.quantity > 1 && <> · Total <b>₹{(best.effective_price * tender.quantity).toFixed(2)}</b></>}</div>
            </div>
            {best.discount_pct > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#1B5E20]">Discount</div>
                <div className="font-heading font-bold text-lg">{best.discount_pct}% off <span className="text-xs text-ink/50 line-through ml-1">₹{best.price}</span></div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-ink/50">Quality</div>
              <div className="font-semibold capitalize"><Award className="w-3.5 h-3.5 inline mr-1 text-[#9A7B0A]"/>{qualityLabel[best.quality_grade] || best.quality_grade}</div>
            </div>
            {best.delivery_minutes && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink/50">Delivery</div>
                <div className="font-semibold"><Truck className="w-3.5 h-3.5 inline mr-1 text-[#0D47A1]"/>~{best.delivery_minutes} min</div>
              </div>
            )}
          </div>
          {best.offer_text && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-[#E67E22] text-[#D35400] px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3"/>{best.offer_text}
            </div>
          )}
          {open && (
            <button data-testid={`accept-tender-${tender.id}`} onClick={onAccept} className="btn-primary mt-5 w-full md:w-auto"><CheckCircle2 className="w-4 h-4"/>Accept this deal & order</button>
          )}
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-border bg-white p-5 text-sm text-ink/60">
          No bids yet. We've notified local shop owners; first bid should appear within a few minutes.
        </div>
      )}

      {awarded && tender.order_id && (
        <Link to={`/orders/${tender.order_id}`} data-testid={`view-awarded-${tender.id}`} className="btn-outline mt-4">View your order →</Link>
      )}
    </article>
  );
}

function AcceptDialog({ tender, onClose, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState({ full_name: '', phone: '', line1: '', village: tender?.village || '', landmark: '' });

  useEffect(() => {
    if (tender) setAddr(a => ({ ...a, village: tender.village || '' }));
  }, [tender]);

  if (!tender) return null;

  const place = async () => {
    if (!addr.full_name || !addr.phone || !addr.line1 || !addr.village) { toast.error('Please fill the address'); return; }
    setBusy(true);
    try {
      const order = await tendersApi.accept(tender.id, { address: addr, payment_method: 'cod' });
      onSuccess(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place order');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!tender} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Accept best deal</DialogTitle>
          <DialogDescription>Confirming will reveal the winning shop and place an order for <b>{tender.product_name}</b> at ₹{tender.best_bid?.effective_price} × {tender.quantity}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <input data-testid="accept-name" placeholder="Full name" value={addr.full_name} onChange={e=>setAddr({...addr, full_name:e.target.value})} className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"/>
          <input data-testid="accept-phone" placeholder="Phone" value={addr.phone} onChange={e=>setAddr({...addr, phone:e.target.value})} className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"/>
          <input data-testid="accept-line1" placeholder="House / Street" value={addr.line1} onChange={e=>setAddr({...addr, line1:e.target.value})} className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"/>
          <input data-testid="accept-village" placeholder="Village" value={addr.village} onChange={e=>setAddr({...addr, village:e.target.value})} className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"/>
          <input data-testid="accept-landmark" placeholder="Landmark (optional)" value={addr.landmark} onChange={e=>setAddr({...addr, landmark:e.target.value})} className="w-full border border-border bg-white rounded-lg px-3 py-2.5 text-sm"/>
        </div>
        <div className="text-xs text-ink/60 mt-2">Payment method: <b>Cash on Delivery</b>. You can opt for Stripe/Razorpay from the order page later.</div>
        <button data-testid="confirm-accept" disabled={busy} onClick={place} className="btn-primary w-full mt-3">{busy ? 'Placing order…' : 'Confirm & place order'}</button>
      </DialogContent>
    </Dialog>
  );
}
